import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * useCompanyMembers
 *
 * Subscribes to `/companies/{companyId}/users` and returns a UID → display
 * descriptor map for ALL members (operators, technicians, owners, admins).
 *
 * Intended use: looking up the name of the operator who initiated a call
 * or sent an internal message, so the UI can render "Will" instead of
 * "wbatts47@gmail.com" on call-event pills.
 *
 * Display-name fallback chain (matches the pattern used in
 * `useCompanyTechnicians`):
 *   1. `name`         (explicit, preferred)
 *   2. `displayName`  (some tenants store it this way)
 *   3. email local-part (`user@example.com` → `user`)
 *   4. the uid itself (last-resort — always a non-empty string)
 *
 * Returns:
 *   { membersById: { [uid]: { name, email, role } }, loading, error }
 */
export function useCompanyMembers(companyId) {
  const [membersById, setMembersById] = useState({});
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    if (!companyId || !db) {
      setMembersById({});
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const ref = collection(db, 'companies', companyId, 'users');

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = {};
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const email = (data.email || '').trim();
          const raw = (data.name || data.displayName || '').trim();
          const name = raw || (email ? email.split('@')[0] : docSnap.id);
          next[docSnap.id] = {
            name,
            email,
            role: (data.role || '').toLowerCase(),
          };
        });
        setMembersById(next);
        setLoading(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useCompanyMembers] snapshot error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [companyId]);

  return { membersById, loading, error };
}

/**
 * Pure helper — resolves a display name from either a uid lookup or a
 * fallback email. Exported for components that can't use the hook
 * (e.g. snapshot-driven renderers inside tight loops).
 */
export function resolveOperatorName({ uid, email, membersById }) {
  if (uid && membersById && membersById[uid]?.name) {
    return membersById[uid].name;
  }
  const e = (email || '').trim();
  if (e) return e.split('@')[0] || e;
  return 'Operator';
}

export default useCompanyMembers;
